import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Facebook, Instagram, Phone, MapPin, Clock, Mail } from 'lucide-react';
import { SITE_DATA } from '../data/Constants';

const Footer = () => {
  const location = useLocation();

  return (
    <footer className='bg-primary text-white pt-10 md:pt-15 pb-4 border-t border-white/10 font-sans'>
      <div className='max-w-7xl mx-auto px-6 lg:px-12'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-0 lg:gap-16 mb-10 md:mb-15 text-center md:text-left'>
          <div className='flex flex-col items-center md:items-start space-y-8'>
            <Link to="/" className='block bg-white text-primary max-w-47 lg:max-w-55 px-4 py-3 rounded-xl shadow-lg hover:scale-105 transition-transform duration-300'>
              <img
                src={SITE_DATA.brandLogo}
                alt={SITE_DATA.brandName}
                width={220}
                height={48}
                className='h-10 md:h-12 w-auto object-contain'
              />
            </Link>

            <div className='space-y-6'>
              <a
                href={SITE_DATA.mapLink}
                target="_blank"
                rel="noopener noreferrer"
                className='flex flex-col md:flex-row items-center md:items-start gap-3 text-white/90 text-sm font-medium hover:text-secondary transition-colors group'
              >
                <div className='w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-secondary group-hover:text-white transition-colors'>
                  <MapPin className='w-4 h-4' />
                </div>
                <span>20771 Langley Bypass #201<br />{SITE_DATA.location} V3A 5E8</span>
              </a>

              <div className='flex flex-col md:flex-row items-center md:items-start gap-3 text-white/90 text-sm font-medium'>
                <div className='w-10 h-10 rounded-full bg-white/10 flex items-center justify-center'>
                  <Clock className='w-4 h-4' />
                </div>
                <span>Mon-Fri: 09:00am - 04:30pm<br />Saturday: 10:00am - 04:00pm</span>
              </div>
            </div>
          </div>

          <div className='flex flex-col items-center md:items-center'>
            <h3 className='font-heading text-2xl text-white uppercase mb-8 tracking-wide font-bold'>
              Explore
            </h3>
            <ul className='space-y-4'>
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Products', path: '/products' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className={`flex items-center gap-2 transition-colors group font-medium ${isActive ? 'text-white' : 'text-white/90 hover:text-white'}`}
                    >
                      <span
                        className={`text-secondary transition-all duration-300 font-bold text-lg leading-none pb-1
                          ${isActive ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0'}
                        `}
                      >
                        ›
                      </span>
                      <span className={`transition-transform duration-300 ${isActive ? 'translate-x-1' : 'group-hover:translate-x-1'}`}>
                        {link.name}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className='flex flex-col items-center md:items-center'>
            <h3 className='font-heading text-2xl text-white uppercase tracking-wide font-bold'>
              Contact
            </h3>

            <div className='text-left md:text-left mt-6'>
              <a href={`tel:${SITE_DATA.phone}`} className='flex items-center justify-center md:justify-start gap-3 text-2xl md:text-2xl font-heading font-bold text-white hover:text-secondary transition-colors group'>
                <div className='w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-secondary group-hover:text-white transition-colors'>
                  <Phone className='w-4 h-4' fill='currentColor' />
                </div>

                {SITE_DATA.phone}
              </a>
            </div>

            <a
              href={`mailto:${SITE_DATA.email}`}
              target="_blank"
              rel="noopener noreferrer"
              className='flex items-center mt-4 gap-3 text-white/90 text-sm font-medium hover:text-secondary transition-colors group'
            >
              <div className='w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-secondary group-hover:text-white transition-colors'>
                <Mail className='w-5 h-5' />
              </div>
              <span>{SITE_DATA.email}</span>
            </a>

            <div className='flex gap-4 mt-4'>
              {[
                { Icon: Facebook, link: "https://www.facebook.com/p/Mainland-Fireplaces-Outdoor-Kitchens-61554229104262/", name: "Facebook" },
                { Icon: Instagram, link: "https://www.instagram.com/mainlandfireplaces", name: "Instagram" },
              ].map((item, i) => (
                <a
                  key={i}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit our ${item.name} page`}
                  className='w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-secondary hover:border-secondary hover:text-white transition-all duration-300 cursor-pointer bg-white/10'
                >
                  <item.Icon className='w-5 h-5' />
                </a>
              ))}
            </div>
          </div>

        </div>

        <div className='pt-6 border-t border-white/20 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left'>
          <p className='text-xs text-white/90 font-medium'>
            © {new Date().getFullYear()} {SITE_DATA.brandName}. All Rights Reserved.
          </p>
          <div className='flex items-center gap-4 text-xs text-white font-medium'>
            <Link to="/privacy-policy" className='hover:text-secondary transition-colors underline underline-offset-4 decoration-white/30'>Privacy Policy</Link>
            <span className='opacity-50 text-white/50'>|</span>
            <Link to="/terms-and-conditions" className='hover:text-secondary transition-colors underline underline-offset-4 decoration-white/30'>Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;